import React, { useLayoutEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { select } from 'd3-selection';
import { drag, D3DragEvent } from 'd3-drag';
import { scaleLinear } from 'd3-scale';
import 'd3-transition';

const Circle = styled.circle`
  cursor: pointer;
`;

const Text = styled.text`
  font-size: 0.75rem;
  fill: #0076ce;
  text-anchor: middle;
`;

interface Props {
  radius: number;
  color: string;
  strokeColor: string;
  strokeWidth: string | number;
  cx: number;
  cy: number;
  widthBoundary: number;
  clsName: string;
  onMoveHandle: (x: number, y: number) => void;
}
const handleDiameter = 16;

const ZoomHandle = (props: Props) => {
  const {
    radius,
    color,
    strokeColor,
    strokeWidth,
    cx,
    cy,
    widthBoundary,
    clsName,
    onMoveHandle,
  } = props;

  const handle = useRef(null);
  const handleText = useRef(null);

  const xScale = scaleLinear()
    .domain([0, 100])
    .range([0, widthBoundary]);

  const [textValue, setTextValue] = useState(0);

  function dragended(this: Element) {
    const x1 =
      clsName === 'left'
        ? Number.parseInt(select(this).attr('cx'))
        : Number.parseInt(select('circle.zoom-handle-left').attr('cx'));
    const x2 =
      clsName === 'left'
        ? Number.parseInt(select('circle.zoom-handle-right').attr('cx'))
        : Number.parseInt(select(this).attr('cx'));
    onMoveHandle(Math.round(xScale.invert(x1)), Math.round(xScale.invert(x2)));
  }

  function dragged(this: Element, event: D3DragEvent<SVGCircleElement, unknown, unknown>) {
    const otherHandle =
      clsName === 'left'
        ? Number.parseInt(select('circle.zoom-handle-right').attr('cx'))
        : Number.parseInt(select('circle.zoom-handle-left').attr('cx'));

    if (event.x > 0 && event.x <= widthBoundary) {
      // collision detection
      if (
        (clsName === 'left' && event.x < otherHandle - handleDiameter) ||
        (clsName === 'right' && event.x > otherHandle + handleDiameter)
      ) {
        select(this).attr('cx', event.x);
        select(`text.zoom-handle-text-${clsName}`).attr('x', event.x);
        setTextValue(xScale.invert(event.x));
      }
    }
  }

  useLayoutEffect(() => {
    select(`circle.zoom-handle-${clsName}`).call(
      drag()
        .on('drag', dragged)
        .on('end', dragended)
    );

    // reset scales
    onMoveHandle(0, 100);

    // reset handles
    if (clsName === 'left') {
      select(handle.current)
        .transition()
        .duration(500)
        .attr('cx', 0);

      select(handleText.current)
        .transition()
        .duration(500)
        .attr('x', 0);
    } else {
      select(handle.current)
        .transition()
        .duration(500)
        .attr('cx', widthBoundary);

      select(handleText.current)
        .transition()
        .duration(500)
        .attr('x', widthBoundary);
    }

    setTextValue(xScale.invert(cx));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widthBoundary]);

  return (
    <g>
      <Circle
        ref={handle}
        className={`zoom-handle-${clsName}`}
        r={radius}
        fill={color}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        cx={cx}
        cy={cy}
      />

      <Text ref={handleText} className={`zoom-handle-text-${clsName}`} x={cx} y={cy} dy={25}>
        {`${Math.round(textValue)}%`}
      </Text>
    </g>
  );
};

export default ZoomHandle;
